import { categoryLabel } from './storyType';

/** Judged/candidate counters streamed by a search run's status polls. */
export interface SearchRunProgress {
  judged: number;
  candidates: number;
}

export interface SearchPollPoint {
  elapsedMs: number;
  cachedResults: number;
  newPositives: number;
  newNegatives: number;
  complete: boolean;
}

/** Keep the chart readable for fast searches while allowing slow tails to expand it. */
export function searchTimelineDurationMs(points: SearchPollPoint[]): number {
  const latestElapsedMs = Math.max(0, ...points.map((point) => point.elapsedMs));
  return Math.max(10_000, Math.ceil(latestElapsedMs / 5_000) * 5_000);
}

interface SearchPollCounters {
  cachedRelevant: number;
  liveRelevant: number;
  liveNotRelevant: number;
  complete: boolean;
}

/** Convert cumulative server counters into the work newly visible in this poll. */
export function buildSearchPollPoint(
  elapsedMs: number,
  current: SearchPollCounters,
  previous?: SearchPollCounters,
): SearchPollPoint {
  return {
    elapsedMs: Math.max(0, elapsedMs),
    cachedResults: current.cachedRelevant,
    newPositives: Math.max(0, current.liveRelevant - (previous?.liveRelevant ?? 0)),
    newNegatives: Math.max(0, current.liveNotRelevant - (previous?.liveNotRelevant ?? 0)),
    complete: current.complete,
  };
}

/**
 * Toolbar label while a live run populates. Plain "searching..." until the
 * run has assembled its candidate pool (candidates arrive as 0 on the first
 * polls), then the run's own judged/candidate counters.
 */
export function searchingLabel(progress: SearchRunProgress | null): string {
  if (!progress || progress.candidates <= 0) return 'searching...';
  const judged = Math.min(progress.judged, progress.candidates);
  return `checking ${judged.toLocaleString()} of ${progress.candidates.toLocaleString()}...`;
}

/**
 * Label for the background incremental refresh of a restored run, whose
 * candidate pool is only the articles ingested since the restored results.
 */
export function refreshingLabel(progress: SearchRunProgress | null): string {
  if (!progress || progress.candidates <= 0) return 'updating…';
  const noun = progress.candidates === 1 ? 'article' : 'articles';
  return `checking ${progress.candidates.toLocaleString()} new ${noun}…`;
}

export function describeActiveFilters(filters: {
  excludeCategories: string[];
  readingMinutesMin: number | null;
  readingMinutesMax: number | null;
}): string[] {
  const parts: string[] = [];
  if (filters.excludeCategories.length > 0) {
    parts.push(`excluding ${filters.excludeCategories.map(categoryLabel).join(', ')}`);
  }
  if (filters.readingMinutesMax != null) {
    parts.push(`<= ${filters.readingMinutesMax} min read`);
  }
  if (filters.readingMinutesMin != null) {
    parts.push(`>= ${filters.readingMinutesMin} min read`);
  }
  return parts;
}


/** A search's identity, used to tell a re-submit from a repeat.
 *
 * The composer refuses to start a run identical to the one already in
 * flight, so anything that changes what the server would do has to be in
 * here — including the search's candidate lookback window.
 */
export function searchSnapshot(values: {
  description: string;
  decay: number;
  relevance: number;
  excludeCategories: string[];
  readingMinutesMin: number | null;
  readingMinutesMax: number | null;
  tldrPreviewMode: boolean;
  searchWindowHours?: 24 | 72;
}): string {
  return JSON.stringify({
    description: values.description.trim(),
    decay: values.decay,
    relevance: values.relevance,
    excludeCategories: values.excludeCategories,
    readingMinutesMin: values.readingMinutesMin,
    readingMinutesMax: values.readingMinutesMax,
    tldrPreviewMode: values.tldrPreviewMode,
    searchWindowHours: values.searchWindowHours ?? 24,
  });
}
